import { create } from 'zustand';
import { api } from '@/core/api/apiClient';
import type { TaskDetail } from '@cg-projects/shared';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { nav } from '@/core/router/navIntent';
import { useToastStore } from '@/core/ui/toastStore';

// ---------------------------------------------------------------------------
// Instant open (DESIGN_DIRECTION §5A) — opening a task is the most frequent
// action in the product, and it must not be a spinner. Three layers, cheapest
// answer first:
//   1. detailCache — LRU of the last ~25 fetched TaskDetails (5 min TTL). A hit
//      renders the WHOLE panel instantly, then revalidates via refresh().
//   2. seedFromListItem — the board row the click came from already knows the
//      title/state/assignees/tags/dates; promote it to a partial TaskDetail so
//      the header + rail paint in 0ms while the fetch fills the rest.
//   3. prefetchTask — hover-intent warm-up (row sites wire onMouseEnter), so
//      the click that follows is usually a cache hit.
//
// A SEED IS NOT A FETCH. It carries `writeDate: ''` and an empty
// descriptionHtml the server never sent, so it must NEVER become the baseline
// for a description save — `seeded` marks it, writeDate '' is the tell the
// guards key on, and the panel keeps the description/comments zone as
// skeletons until the real fetch lands.
// ---------------------------------------------------------------------------

const DETAIL_CACHE_MAX = 25;
const DETAIL_CACHE_TTL_MS = 5 * 60_000;
/** id → last-received detail. Map insertion order IS the LRU order: re-insert
 *  on every touch, evict from the front. */
const detailCache = new Map<number, { task: TaskDetail; at: number }>();

function cacheTaskDetail(task: TaskDetail): void {
  detailCache.delete(task.id); // re-insert = most recently used
  detailCache.set(task.id, { task, at: Date.now() });
  if (detailCache.size > DETAIL_CACHE_MAX) {
    const oldest = detailCache.keys().next().value;
    if (oldest !== undefined) detailCache.delete(oldest);
  }
}

function cachedDetail(id: number): TaskDetail | null {
  const hit = detailCache.get(id);
  if (!hit) return null;
  if (Date.now() - hit.at > DETAIL_CACHE_TTL_MS) { detailCache.delete(id); return null; }
  return hit.task;
}

/** Read-only peek at the instant-open cache. The palette's verb layer
 *  (DESIGN_DIRECTION §5C) resolves a task's CURRENT assignees/priority/stage
 *  through this before falling back to a fetch — hover-prefetch has usually
 *  warmed it already. Never mutates; never extends an entry's life. */
export function peekTaskDetail(id: number): TaskDetail | null {
  return cachedDetail(id);
}

// ---------------------------------------------------------------------------
// Recent-tasks ring (SHARED CONTRACT — DESIGN_DIRECTION §5C):
// localStorage 'cgp:recent-tasks' = JSON array of {id, name, areaId}, max 15,
// newest first. The WRITER lives HERE, on task open, by choice: every door
// into a task — board card, inbox row, notification toast, deep link, search
// pick — funnels through fetchInto, so the ring reflects what you actually
// worked on, not only what the palette opened. READER: SearchModal's
// zero-query palette. Best-effort top to bottom: a full/absent localStorage
// (or the Node test run, which has none) must never break an open.
// ---------------------------------------------------------------------------
const RECENT_TASKS_KEY = 'cgp:recent-tasks';
const RECENT_TASKS_MAX = 15;

function pushRecentTask(t: { id: number; name: string; areaId: number }): void {
  try {
    const raw = localStorage.getItem(RECENT_TASKS_KEY);
    const prev = raw ? (JSON.parse(raw) as unknown) : [];
    const kept = (Array.isArray(prev) ? prev : []).filter(
      (r): r is { id: number; name: string; areaId: number } =>
        !!r && typeof (r as { id?: unknown }).id === 'number' && (r as { id: number }).id !== t.id,
    );
    const next = [{ id: t.id, name: t.name, areaId: t.areaId }, ...kept].slice(0, RECENT_TASKS_MAX);
    localStorage.setItem(RECENT_TASKS_KEY, JSON.stringify(next));
  } catch { /* best-effort — see above */ }
}

/** The board row the click came from, promoted to a renderable partial detail.
 *  Everything the row holds is REAL (title/state/assignees/tags/dates); the
 *  detail-only fields are deliberately blank — see the seed warning above. */
function seedFromListItem(id: number): TaskDetail | null {
  for (const list of Object.values(useTasksStore.getState().tasksByArea)) {
    const row = list.find((t) => t.id === id);
    if (row) return { ...row, descriptionHtml: '', writeDate: '', children: [], blockedBy: [], blocks: [] };
  }
  return null;
}

/** Fire-and-forget cache warm-up for hover-intent (row sites wire onMouseEnter).
 *  Deduped while in flight; a still-fresh entry or the already-open task is left
 *  alone; errors are swallowed — a failed prefetch just means the eventual open
 *  fetches normally and surfaces the error itself. */
const prefetchInFlight = new Set<number>();
export function prefetchTask(id: number): void {
  if (prefetchInFlight.has(id) || cachedDetail(id)) return;
  if (useTaskDetailStore.getState().openId === id) return; // open = already freshest
  prefetchInFlight.add(id);
  api.get<{ task: TaskDetail }>(`/tasks/${id}`)
    .then(({ task }) => cacheTaskDetail(task))
    .catch(() => { /* swallow — see above */ })
    .finally(() => prefetchInFlight.delete(id));
}

interface TaskDetailState {
  openId: number | null;
  task: TaskDetail | null;
  loading: boolean;
  error: string | null;
  /** True while `task` is a list-item SEED (instant open) rather than a fetched
   *  detail. A seed must never feed a save baseline or the detail cache — it has
   *  writeDate '' and an empty descriptionHtml the server never sent. Cleared
   *  the moment any real TaskDetail lands. */
  seeded: boolean;
  /** True while the description editor holds unsaved text — EVERY navigation
   *  into another task must confirm, not just Esc/✕ (a notify toast's "Open"
   *  arriving mid-sentence must never eat 20 minutes of writing). */
  dirty: boolean;
  setDirty: (v: boolean) => void;
  /** The SYNCHRONOUS half of open()'s veto (false = the user kept their draft).
   *  Any caller that pairs this open with another store mutation MUST resolve the
   *  veto through here, BEFORE nav.tx: tx flushes in its own `finally`, so a
   *  `.then` continuation lands after the history write and pushes a second entry. */
  confirmOpen: (id: number) => boolean;
  open: (id: number) => Promise<boolean>;
  close: () => void;
  /** Route apply: open WITHOUT the discard confirm. A blocking dialog must never
   *  run in the popstate path — popstate isn't cancelable, so by the time it fires
   *  the URL has already moved and a "Cancel" would desync state from the URL. */
  openFromRoute: (id: number) => Promise<void>;
  /** Route apply: close WITHOUT the confirm, and KEEP the draft on disk so a
   *  hardware-Back can never destroy unsaved writing. Toasts if there was one. */
  closeFromRoute: () => void;
  /** The project the open task sat on WHEN THE PANEL OPENED — null when nothing
   *  is open, or when a cold deep link hasn't answered yet.
   *
   *  A card can leave the board you are standing on while you are looking at it:
   *  approving at the last step of Short Form Content Team graduates it to
   *  Content Calendar. router.dismissTask() compares this with the task's CURRENT
   *  project so closing puts you back where you were standing instead of following
   *  the card onto a board you never chose (Allyson, 2026-09-23). In-panel travel
   *  to another task re-enters fetchInto and re-stamps this, so a blocked-by hop
   *  across projects is unaffected. */
  openedAreaId: number | null;
  /** One-shot: the comment a notification pointed at. TaskComments scrolls
   *  to it, flashes it, then clears it ("the inbox delivered you to the
   *  building, not the room" — the phone release fixes the room). */
  focusMessageId: number | null;
  setFocusMessage: (id: number | null) => void;
  /** Shared fetch body for open()/openFromRoute(). Sets openId synchronously. */
  fetchInto: (id: number) => Promise<void>;
  /** Refetch the open task WITHOUT unmounting the panel (refreshes writeDate
   *  after a save conflict; the editor and its text stay put). */
  refresh: () => Promise<void>;
}

export const useTaskDetailStore = create<TaskDetailState>()((set, get) => ({
  openId: null,
  focusMessageId: null,
  task: null,
  loading: false,
  error: null,
  seeded: false,
  dirty: false,
  openedAreaId: null,

  setDirty: (v) => set({ dirty: v }),
  setFocusMessage: (focusMessageId) => set({ focusMessageId }),

  confirmOpen: (id) => {
    // Re-opening the task already on screen must never pop a discard prompt.
    if (get().openId === id && get().task) return true;
    if (get().dirty) {
      if (!window.confirm('You have unsaved description edits — discard them?')) return false;
      // Discard means discard — drop the cached draft of the task we're LEAVING,
      // or it would resurrect and overwrite canonical text next time it's opened.
      const leaving = get().openId;
      if (leaving !== null) { try { localStorage.removeItem(`cgp:draft:desc:${leaving}`); } catch { /* ignore */ } }
    }
    return true;
  },

  open: async (id) => {
    const already = get().openId === id && get().task !== null;
    if (!get().confirmOpen(id)) return false;
    if (already) return true;
    // The push below is for DOORS ONLY — board card, table row, calendar chip,
    // My Work / inbox / search / report row, toast, context menu: entering the
    // panel is one history entry. Task-to-task travel INSIDE the open panel
    // (sub-task row, parent link, prev/next, blocked-by refs, duplicate) must
    // wrap this call in nav.tx('replace', …) so the panel keeps that single
    // entry — otherwise ✕ (which consumes the entry) retraces every hop like a
    // Back button. Inside a tx this mark is neutralized and the tx's mode wins.
    nav.mark('push'); // opening a task IS a place you can Back out of
    await get().fetchInto(id);
    return true;
  },

  openFromRoute: async (id) => {
    // openId is set SYNCHRONOUSLY before the first await, so the router can
    // `void` this and still get one single history write for the whole apply.
    await get().fetchInto(id);
  },

  fetchInto: async (id) => {
    // §5A: answer from what we already hold before going to the network.
    const cached = cachedDetail(id);
    if (cached) {
      // A cached detail is a real, fetched task — render the whole panel from
      // it (no skeletons, no spinner) and revalidate through the existing
      // refresh path, which never unmounts the panel and only writes back if
      // this task is still the open one.
      set({ openId: id, task: cached, seeded: false, loading: false, error: null, dirty: false, openedAreaId: cached.areaId });
      pushRecentTask(cached);
      void get().refresh();
      return;
    }
    const seed = seedFromListItem(id);
    // With a seed the header/rail paint instantly and loading stays true so the
    // panel holds skeletons over description/comments; with none (cold deep
    // link) task stays null and the full-panel spinner survives.
    // A seed already knows the project (it came off the board row), so the
    // "where I opened it" stamp is right from the first paint; a cold deep link
    // has to wait for the fetch below.
    set({ openId: id, task: seed, seeded: seed !== null, loading: true, error: null, dirty: false, openedAreaId: seed?.areaId ?? null });
    if (seed) pushRecentTask(seed);
    try {
      const { task } = await api.get<{ task: TaskDetail }>(`/tasks/${id}`);
      // ?? not = : a seeded open stamped the project already; only a cold deep
      // link is still unstamped, and this fetch is its FIRST sight of the card.
      // Never re-stamp from a later read — that would erase the move we exist to see.
      set((s) => (s.openId === id ? { task, seeded: false, loading: false, openedAreaId: s.openedAreaId ?? task.areaId } : s));
      // Cold deep links reach here with no seed pushed; a seeded open re-pushes
      // the same id, which just refreshes the stored name. Both are correct.
      if (get().openId === id) pushRecentTask(task);
    } catch (err) {
      set((s) =>
        s.openId === id
          ? {
              // A seed can't stay on screen with its fetch dead — it has no
              // description to show and nothing on it is safely editable. Fall
              // back to the plain error screen. A REAL task (a concurrent
              // mutation's reconcile landed meanwhile) stays put instead.
              task: s.task && s.task.writeDate ? s.task : null,
              seeded: false,
              loading: false,
              error: err instanceof Error ? err.message : 'Failed to load task.',
            }
          : s,
      );
    }
  },

  close: () => set({ openId: null, task: null, loading: false, error: null, seeded: false, dirty: false, openedAreaId: null }),

  closeFromRoute: () => {
    // Deliberately does NOT delete cgp:draft:desc:<id> (unlike open()'s discard
    // branch). Hardware Back is not a decision to throw work away.
    if (get().dirty) {
      useToastStore.getState().show('Draft kept — reopen the task to finish it.', 'info');
    }
    set({ openId: null, task: null, loading: false, error: null, seeded: false, dirty: false, openedAreaId: null });
  },

  refresh: async () => {
    const id = get().openId;
    if (id === null) return;
    try {
      const { task } = await api.get<{ task: TaskDetail }>(`/tasks/${id}`);
      set((s) => (s.openId === id ? { task, seeded: false } : s));
    } catch { /* keep what we have */ }
  },
}));

// Write-through: every REAL TaskDetail the store receives lands in the cache —
// not just fetchInto/refresh above, but writes applied from OUTSIDE this file
// too (mutations.applyServerTask reconciles after every patch/move/create; the
// panel's task:sync handler applies teammates' live writes). One subscription
// instead of a call at each site means a reopen within TTL always paints the
// newest version this client has seen, and a write path added later is covered
// by default. Seeds are excluded — writeDate '' is the tell.
useTaskDetailStore.subscribe((s, prev) => {
  if (s.task && s.task !== prev.task && !s.seeded && s.task.writeDate) cacheTaskDetail(s.task);
});
